import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, Inbox, Images, FileText, Star, Settings, LogOut, ExternalLink, Menu } from 'lucide-react';
import Logo from '../../components/Logo';
import { useAuth } from '../../context/AuthContext';
import LeadsPanel from '../../components/admin/LeadsPanel';
import ProjectsPanel from '../../components/admin/ProjectsPanel';
import ContentPanel from '../../components/admin/ContentPanel';
import ReviewsPanel from '../../components/admin/ReviewsPanel';
import SettingsPanel from '../../components/admin/SettingsPanel';

const TABS = [
  { key: 'leads', label: 'Estimate Requests', icon: Inbox },
  { key: 'projects', label: 'Projects & Gallery', icon: Images },
  { key: 'content', label: 'Website Content', icon: FileText },
  { key: 'reviews', label: 'Reviews', icon: Star },
  { key: 'settings', label: 'Settings', icon: Settings },
];

const Dashboard = () => {
  const { logout, username } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('leads');
  const [mobileNav, setMobileNav] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  const NavItems = ({ onClick }) => (
    <nav className="space-y-1">
      {TABS.map((t) => {
        const Icon = t.icon;
        const active = tab === t.key;
        return (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); onClick && onClick(); }}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-[14px] font-medium transition-colors ${
              active ? 'bg-[#86c93c] text-[#123020]' : 'text-[#c5d6bd] hover:bg-[#1b4029] hover:text-white'
            }`}
          >
            <Icon className="h-4 w-4" /> {t.label}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#f5f3ea] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-[260px] bg-[#123020] shrink-0 fixed inset-y-0">
        <div className="px-6 py-5 border-b border-[#1b4029]"><Logo light compact /></div>
        <div className="flex-1 p-4 overflow-y-auto"><NavItems /></div>
        <div className="p-4 border-t border-[#1b4029] space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-[14px] text-[#c5d6bd] hover:bg-[#1b4029] hover:text-white transition-colors">
            <ExternalLink className="h-4 w-4" /> View Website
          </a>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-[14px] text-[#e79b9b] hover:bg-[#3a1f1f] transition-colors">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#123020] flex items-center justify-between px-4 h-16">
        <Logo light compact />
        <button onClick={() => setMobileNav((v) => !v)} className="h-10 w-10 flex items-center justify-center text-white"><Menu className="h-6 w-6" /></button>
      </div>
      {mobileNav && (
        <div className="lg:hidden fixed top-16 inset-x-0 z-40 bg-[#123020] p-4 shadow-lg">
          <NavItems onClick={() => setMobileNav(false)} />
          <div className="mt-2 pt-2 border-t border-[#1b4029] space-y-1">
            <a href="/" target="_blank" rel="noreferrer" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-[14px] text-[#c5d6bd] hover:bg-[#1b4029]"><ExternalLink className="h-4 w-4" /> View Website</a>
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-[14px] text-[#e79b9b]"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 lg:ml-[260px] pt-16 lg:pt-0">
        <div className="hidden lg:flex items-center justify-between px-8 h-16 bg-white border-b border-[#e6e4d7]">
          <div className="flex items-center gap-2 text-[#123020] font-medium"><LayoutDashboard className="h-5 w-5 text-[#5f9e2c]" /> Admin Dashboard</div>
          <span className="text-[14px] text-[#5a6b60]">Signed in as <b className="text-[#123020]">{username}</b></span>
        </div>
        <div className="p-5 lg:p-8 max-w-[1100px]">
          {tab === 'leads' && <LeadsPanel />}
          {tab === 'projects' && <ProjectsPanel />}
          {tab === 'content' && <ContentPanel />}
          {tab === 'reviews' && <ReviewsPanel />}
          {tab === 'settings' && <SettingsPanel />}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
